// public/js/features/tracker-config/asset-config-section.js
// The "Tracker configuration" section on the asset detail page: assign a template
// to the asset, see each paired device's config twin (desired vs reported, with a
// pending/applied/failed status), override a single device, and — while the DM
// push is stubbed — simulate a device check-in. See docs/tracker-config.md.
import { loadTemplates, loadAssetConfig, assignAssetTemplate, setDeviceOverride, simulateCheckin } from './api.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { helpIcon, initHelp } from '../../ui/help.js';
import { createBatteryPanel } from './battery-ui.js';
import { familyForModel } from './battery.js';
import { brandLabelForFamily } from '../devices/branded-models.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const STATUS = {
  pending: ['text-bg-warning', 'Pending'], applied: ['text-bg-success', 'Applied'],
  failed: ['text-bg-danger', 'Failed'], unknown: ['text-bg-secondary', 'Not configured'],
};

export async function renderAssetConfigSection(container, assetUUID) {
  if (!container) return;
  const groups = getCurrentUser().groups || [];
  if (!can(groups, 'manageDeviceConfig')) { container.innerHTML = ''; return; }
  const isSuper = groups.includes('superadmin') || groups.includes('admin') || groups.includes('platformSupport');

  container.innerHTML = `<div class="text-muted small py-2"><span class="spinner-border spinner-border-sm me-2"></span>Loading configuration…</div>`;
  let cfg, tpls;
  try { [cfg, tpls] = await Promise.all([loadAssetConfig(assetUUID), loadTemplates()]); }
  catch { container.innerHTML = ''; return; }

  if (!cfg.devices.length) { container.innerHTML = ''; return; }   // no trackers → nothing to configure
  const settings = tpls.settings || [];
  const helpDict = Object.fromEntries(settings.map(s => [`cfg.${s.key}`, s.help]));
  const available = [...(tpls.platform || []), ...(tpls.tenant || [])];
  const fmt = (k, v) => { const s = settings.find(x => x.key === k); return s ? (s.type === 'boolean' ? (v ? 'On' : 'Off') : `${v}${s.unit ? ' ' + s.unit : ''}`) : v; };
  const options = (sel, noneLabel) => `<option value="">${noneLabel}</option>` +
    available.map(t => `<option value="${t.templateId}"${sel === t.templateId ? ' selected' : ''}>${esc(t.name)}${t.scope === 'platform' ? ' (platform)' : ''}</option>`).join('');

  const desiredSummary = (d) => d.desired
    ? Object.entries(d.desired).map(([k, v]) => `<span class="badge text-bg-light border me-1 mb-1">${esc((settings.find(s => s.key === k)?.label) || k)}: ${esc(fmt(k, v))}</span>`).join('')
    : '<span class="text-muted small">No template — tracker left unchanged.</span>';

  container.innerHTML = `
    <div class="card shadow-sm">
      <div class="card-header py-2 d-flex justify-content-between align-items-center">
        <span class="fw-semibold"><i class="fa-solid fa-sliders me-2"></i>Tracker configuration</span>
      </div>
      <div class="card-body">
        <div class="row g-2 align-items-end mb-3">
          <div class="col-sm-8">
            <label class="form-label small fw-semibold mb-1">Asset template${helpIcon('cfg.assetTemplate')}</label>
            <select id="ac-asset-tpl" class="form-select form-select-sm">${options(cfg.assetTemplateId, 'None — leave trackers unchanged')}</select>
          </div>
          <div class="col-sm-4 text-muted small">Applies to every paired tracker that has no own override.</div>
        </div>
        <div class="small fw-semibold text-secondary mb-2">Paired trackers</div>
        ${cfg.devices.map(d => {
          const st = STATUS[d.twin?.status || 'unknown'] || STATUS.unknown;
          const drift = d.twin?.reported && d.desired && JSON.stringify(d.twin.reported.settings) !== JSON.stringify(d.desired);
          return `
          <div class="border rounded p-2 mb-2" data-device="${esc(d.deviceId)}">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <span class="fw-semibold">${esc(d.deviceId)}</span>
                <span class="badge ${st[0]} ms-2">${st[1]}</span>
                ${drift ? '<span class="badge text-bg-warning ms-1" title="Reported config differs from desired">drift</span>' : ''}
              </div>
              <div style="min-width:200px;">
                <select class="form-select form-select-sm ac-dev-override" data-device="${esc(d.deviceId)}">
                  ${options(d.overrideTemplateId, 'Follow asset template')}
                </select>
              </div>
            </div>
            <div class="mt-2">${desiredSummary(d)}</div>
            ${d.desired ? `<div class="bat-host mt-2" data-device="${esc(d.deviceId)}"></div>` : ''}
            ${(isSuper && d.twin?.status === 'pending') ? `<div class="mt-2"><button class="btn btn-sm btn-outline-success ac-checkin" data-device="${esc(d.deviceId)}"><i class="fa-solid fa-check me-1"></i>Simulate check-in</button> <span class="text-muted small">(dev: mark the pending config as applied)</span></div>` : ''}
          </div>`;
        }).join('')}
      </div>
    </div>`;

  initHelp(container, { ...helpDict, 'cfg.assetTemplate': 'The template pushed to this asset\'s trackers. Each tracker follows it unless it has its own override below.' });

  // Per-device battery-life estimate from the resolved desired config. Only shown
  // for branded trackers (TracePro/LocatePro); internal test devices get no panel.
  container.querySelectorAll('.bat-host').forEach(hostEl => {
    const d = cfg.devices.find(x => x.deviceId === hostEl.dataset.device);
    const fam = d?.desired ? familyForModel(d.model) : null;
    const brandLabel = fam ? brandLabelForFamily(fam) : null;
    if (!brandLabel) { hostEl.remove(); return; }
    const panel = createBatteryPanel(hostEl, {
      families: [{ key: fam, label: brandLabel }], familyKey: fam,
      familyLocked: true, hideFamilyRow: true, hideNotes: true, conditionsLocked: true,
    });
    panel.refresh(d.desired);
  });

  const reload = () => renderAssetConfigSection(container, assetUUID);
  container.querySelector('#ac-asset-tpl').addEventListener('change', async (e) => {
    try { await assignAssetTemplate(assetUUID, e.target.value || null); window.showToast?.('Template assigned — pushing to trackers', 'success'); reload(); }
    catch (err) { window.showToast?.(err.message, 'danger'); }
  });
  container.querySelectorAll('.ac-dev-override').forEach(sel => sel.addEventListener('change', async () => {
    try { await setDeviceOverride(sel.dataset.device, sel.value || null); window.showToast?.('Override updated', 'success'); reload(); }
    catch (err) { window.showToast?.(err.message, 'danger'); }
  }));
  container.querySelectorAll('.ac-checkin').forEach(b => b.addEventListener('click', async () => {
    try { await simulateCheckin(b.dataset.device); window.showToast?.('Applied (simulated)', 'success'); reload(); }
    catch (err) { window.showToast?.(err.message, 'danger'); }
  }));
}
